// The original transcript of the video (the "Transkript" side of the result card): loaded on
// demand, readable paragraphs grouped by chapter, timestamps that jump into the video, copyable.

import { CircleAlert, Copy, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { SummaryLang, TranscriptMode, VideoTranscript } from '../../shared/api';
import { youTubeWatchUrl } from '../../shared/youtube-url';
import { ApiRequestError, isAbortError } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { formatDuration } from '../lib/format';
import { ERROR_TEXTS } from '../lib/texts';
import { BrowserTranscriptError } from '../lib/transcript';
import { cachedTranscript, loadTranscript } from '../lib/transcriptLoader';
import { transcriptPlainText, transcriptSections } from '../lib/transcriptText';
import { CopyButton } from './CopyButton';

type State = { status: 'loading' } | { status: 'error'; message: string } | { status: 'done'; transcript: VideoTranscript };

export function TranscriptView({
  videoId,
  lang,
  mode,
}: {
  videoId: string;
  lang: SummaryLang | undefined;
  mode: TranscriptMode;
}) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<State & { videoId: string }>(() => {
    const cached = cachedTranscript(videoId);
    return cached ? { videoId, status: 'done', transcript: cached } : { videoId, status: 'loading' };
  });
  const [timestamps, setTimestamps] = useState(true);

  useEffect(() => {
    const cached = cachedTranscript(videoId);
    if (cached) {
      setState({ videoId, status: 'done', transcript: cached });
      return;
    }
    const controller = new AbortController();
    setState({ videoId, status: 'loading' });
    loadTranscript(videoId, { mode, lang, signal: controller.signal }).then(
      (transcript) => setState({ videoId, status: 'done', transcript }),
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState({ videoId, status: 'error', message: errorText(error) });
      },
    );
    return () => controller.abort();
    // `lang`/`mode` only steer how a missing transcript is fetched; a change does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, attempt]);

  const transcript = state.videoId === videoId && state.status === 'done' ? state.transcript : undefined;
  const sections = useMemo(() => (transcript ? transcriptSections(transcript) : []), [transcript]);

  if (state.videoId !== videoId || state.status === 'loading') {
    return (
      <div className="transcript" aria-busy="true">
        <p className="sr-only" role="status">
          Transkript wird geladen
        </p>
        <div className="skeleton-text" aria-hidden="true">
          <span className="skeleton skeleton--line" />
          <span className="skeleton skeleton--line" />
          <span className="skeleton skeleton--line skeleton--mid" />
          <span className="skeleton skeleton--line" />
          <span className="skeleton skeleton--line skeleton--short" />
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="error-panel" role="alert">
        <CircleAlert className="error-panel__icon" size={24} aria-hidden="true" />
        <div className="error-panel__body">
          <h2 className="error-panel__title">Transkript nicht verfügbar</h2>
          <p className="error-panel__message">{state.message}</p>
          <div className="error-panel__actions">
            <button type="button" className="btn btn--secondary" onClick={() => setAttempt((n) => n + 1)}>
              <RefreshCw size={18} aria-hidden="true" />
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const facts = [
    languageName(state.transcript.language),
    state.transcript.isAutoGenerated ? 'automatisch erzeugte Untertitel' : 'Untertitel',
  ].filter(Boolean);

  return (
    <div className="transcript">
      <div className="transcript__bar">
        <p className="transcript__facts">{facts.join(' · ')}</p>
        <label className="transcript__toggle">
          <input type="checkbox" checked={timestamps} onChange={(event) => setTimestamps(event.target.checked)} />
          Zeitstempel
        </label>
        <CopyButton
          label="Transkript kopieren"
          shortLabel="Kopieren"
          icon={<Copy size={17} aria-hidden="true" />}
          onCopy={() => copyText(transcriptPlainText(sections, { timestamps }))}
        />
      </div>

      <div className={`transcript__text${timestamps ? '' : ' transcript__text--plain'}`}>
        {sections.map((section, index) => (
          <section key={`${section.start}-${index}`} className="transcript__section">
            {section.title && (
              <h2 className="transcript__chapter">
                {timestamps && <Timestamp videoId={videoId} seconds={section.start} />}
                {section.title}
              </h2>
            )}
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph.start} className="transcript__paragraph">
                {timestamps && <Timestamp videoId={videoId} seconds={paragraph.start} />}
                {paragraph.text}
              </p>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

function Timestamp({ videoId, seconds }: { videoId: string; seconds: number }) {
  const label = formatDuration(seconds);
  return (
    <a
      className="transcript__time"
      href={`${youTubeWatchUrl(videoId)}&t=${Math.floor(seconds)}s`}
      target="_blank"
      rel="noopener noreferrer"
      title={`Video ab ${label} ansehen`}
    >
      {label}
    </a>
  );
}

const languageNames = (() => {
  try {
    return new Intl.DisplayNames(['de'], { type: 'language' });
  } catch {
    return null;
  }
})();

function languageName(code: string | undefined): string | undefined {
  if (!code) return undefined;
  try {
    return languageNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

function errorText(error: unknown): string {
  if (error instanceof BrowserTranscriptError) {
    if (error.userMessage) return error.userMessage;
    switch (error.code) {
      case 'transcript_unavailable':
        return 'Für dieses Video gibt es keine Untertitel – weder manuell noch automatisch erstellt.';
      case 'video_not_found':
        return ERROR_TEXTS.video_not_found.message;
      case 'blocked':
        return ERROR_TEXTS.transcript_blocked.message;
      case 'failed':
        return ERROR_TEXTS.transcript_browser.message;
    }
  }
  if (error instanceof ApiRequestError) {
    // The server's own text names the reason (no captions, live stream, age restriction).
    if (error.code === 'transcript_unavailable') return error.message;
    if (error.code !== 'aborted') return ERROR_TEXTS[error.code].message;
  }
  return ERROR_TEXTS.transcript_browser.message;
}
